import { CoaExpensesDtoCategory } from 'api/generated/schemas';

import type { AidModelCoaDto } from 'api/generated/schemas';

export interface CoaGroupAmount {
  name: string;
  previousYearAmount: number;
  currentYearAmount: number;
}

export interface CoaSummarySections {
  tuitionFees: CoaGroupAmount[];
  costOfAttendance: CoaGroupAmount[];
}

export interface CoaSectionRow {
  rowType: 'section';
  label: string;
}

export interface CoaGroupRow extends CoaGroupAmount {
  rowType: 'group';
}

export type CoaTableRow = CoaSectionRow | CoaGroupRow;

const TUITION_FEES_CATEGORIES: CoaExpensesDtoCategory[] = [
  CoaExpensesDtoCategory.TUITION,
  CoaExpensesDtoCategory.REQUIRED_FEES,
];

interface YearTotals {
  previousYearAmount: number;
  currentYearAmount: number;
}

const sumExpenseAmounts = (
  expenses: AidModelCoaDto['expenses'],
  categories?: CoaExpensesDtoCategory[]
): YearTotals =>
  (expenses ?? []).reduce<YearTotals>(
    (totals, expense) => {
      if (
        categories &&
        !(expense.category && categories.includes(expense.category))
      ) {
        return totals;
      }

      return {
        previousYearAmount:
          totals.previousYearAmount + (expense.previousYearAmount ?? 0),
        currentYearAmount:
          totals.currentYearAmount + (expense.currentYearAmount ?? 0),
      };
    },
    { previousYearAmount: 0, currentYearAmount: 0 }
  );

export const buildCoaGroupSummaries = (
  coaGroups: AidModelCoaDto[]
): CoaSummarySections => ({
  tuitionFees: coaGroups.map((group) => ({
    name: group.name ?? '',
    ...sumExpenseAmounts(group.expenses, TUITION_FEES_CATEGORIES),
  })),
  costOfAttendance: coaGroups.map((group) => ({
    name: group.name ?? '',
    ...sumExpenseAmounts(group.expenses),
  })),
});

export const buildCoaTableRows = (
  coaGroups: AidModelCoaDto[]
): CoaTableRow[] => {
  const { tuitionFees, costOfAttendance } = buildCoaGroupSummaries(coaGroups);

  return [
    { rowType: 'section', label: 'Tuition & Fees' },
    ...tuitionFees.map(
      (group): CoaGroupRow => ({ rowType: 'group', ...group })
    ),
    { rowType: 'section', label: 'Cost of Attendance' },
    ...costOfAttendance.map(
      (group): CoaGroupRow => ({ rowType: 'group', ...group })
    ),
  ];
};
